/**
 * /settings/pos/printer — pair a Star TSP143IV-BI receipt printer to this
 * browser via Web Bluetooth. Pairing is per-browser / per-device; different
 * workstations pair independently (no server state).
 *
 * Gate: POS is Pro+. Renders RequireFeature around the main body so Starter
 * tenants see the upgrade prompt rather than the pairing UI.
 */
import { RequireFeature } from '@/components/RequireFeature';
import {
  getPairedPrinter,
  kickDrawer,
  pairPrinter,
  printBytes,
  renderReceipt,
  unpairPrinter,
} from '@/lib/escpos';
import { Printer } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Sample receipt for test-print
// ---------------------------------------------------------------------------

function makeSampleReceiptBytes(): Uint8Array {
  return renderReceipt({
    account: { name: 'Your Kennel' },
    facility: { name: 'Main', addressLine: '1 Example Street' },
    transaction: {
      id: 'test_00000000',
      total_minor: 1000,
      tax_minor: null,
      currency: 'GBP',
    },
    lines: [
      {
        description: 'Test item',
        quantity: 1,
        unit_price_minor: 1000,
        line_total_minor: 1000,
      },
    ],
    payments: [{ source: 'cash', amount_minor: 1000 }],
    staffName: 'Test',
    printedAt: new Date().toISOString(),
  });
}

// ---------------------------------------------------------------------------
// Error message helper
// ---------------------------------------------------------------------------

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg === 'web_bluetooth_unsupported') {
    return 'Web Bluetooth is not supported in this browser. Use Chrome or Edge on desktop.';
  }
  if (msg === 'printer_not_paired') {
    return 'Printer is not paired or has been removed from browser permissions. Please pair again.';
  }
  if (msg.includes('sub-spec 12')) {
    return 'Printer pairing is only available in the web app (Chrome/Edge). The native app ships in a future release.';
  }
  return `Error: ${msg}`;
}

// ---------------------------------------------------------------------------
// Inner component (rendered inside RequireFeature gate)
// ---------------------------------------------------------------------------

type PrinterInfo = { deviceId: string; name: string };

function PrinterSettingsBody() {
  const [paired, setPaired] = useState<PrinterInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const result = await getPairedPrinter();
      setPaired(result);
    } catch {
      // If we can't check, treat as unpaired.
      setPaired(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  async function handlePair() {
    setError(null);
    setBusy(true);
    try {
      const result = await pairPrinter();
      setPaired(result);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnpair() {
    setError(null);
    setBusy(true);
    try {
      await unpairPrinter();
      setPaired(null);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleTestPrint() {
    setError(null);
    setBusy(true);
    try {
      const data = makeSampleReceiptBytes();
      await printBytes(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleKickDrawer() {
    setError(null);
    setBusy(true);
    try {
      await kickDrawer();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Printer className="size-5 text-primary" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="h-5 w-40 animate-pulse rounded bg-secondary" />
            ) : paired ? (
              <>
                <p className="text-sm font-medium text-success">Paired: {paired.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground num">{paired.deviceId}</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">No printer paired</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Pair a Star TSP143IV-BI printer to this browser to enable receipt printing.
                </p>
              </>
            )}

            {error && (
              <p className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
        {paired ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleTestPrint()}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 press disabled:opacity-50"
            >
              Test print
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleKickDrawer()}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 press disabled:opacity-50"
            >
              Kick drawer
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleUnpair()}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium text-destructive hover:bg-secondary/80 press disabled:opacity-50"
            >
              Unpair
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void handlePair()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Pairing…' : 'Pair printer'}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PosPrinterSettings() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Printer</h1>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">
          Pair a Star TSP143IV-BI receipt printer to this device. Pairing is per-browser — different
          staff workstations pair independently.
        </p>
      </header>

      <RequireFeature flag="pos">
        <PrinterSettingsBody />
      </RequireFeature>
    </div>
  );
}
